<template>
  <app-page
    :title="
      employee ? $t('employee.terminate.title', { name: employee.full_name }) : $t('employee.terminate.titleFallback')
    "
    :error="error"
    :previous="[
      { label: $t('employee.previousLabel'), to: { name: 'employees' } },
      {
        label: employee ? employee.full_name : $t('employee.title'),
        to: { name: 'employee', params: { employeeId: $route.params.employeeId } },
      },
    ]"
    icon="user-slash"
  >
    <app-loading :loading="loading" />
    <template v-if="!loading && !saved">
      <!-- Guards the page itself, not just the button, so pasting the URL for a blocked
           employee can't reach the form. -->
      <sme-alert v-if="unavailableReason" level="warning">{{ unavailableReason }}</sme-alert>
      <template v-else>
        <p class="text-muted">
          <i18n path="employee.terminate.introBody" tag="span">
            <template #leavingBold
              ><strong>{{ $t('employee.terminate.leavingBold') }}</strong></template
            >
          </i18n>
        </p>
        <p class="text-muted">{{ $t('employee.terminate.markingIntro', { name: employee.full_name }) }}</p>
        <ul class="text-muted">
          <li>{{ $t('employee.terminate.restrictsAccessItem') }}</li>
          <li>
            {{ $t('employee.terminate.unenrolmentItem') }}
          </li>
          <li>
            {{ $t('employee.terminate.manageProductsItem', { productName }) }}
          </li>
        </ul>
        <p>{{ $t('employee.terminate.rejoinBody') }}</p>
        <sme-alert level="info" class="mb-3">
          {{ $t('employee.terminate.integrationWarningAlert', { productName }) }}
        </sme-alert>

        <ValidationObserver v-slot="v">
          <b-form @submit.prevent="v.handleSubmit(onSubmit)">
            <app-input
              v-model="terminationDate"
              type="date"
              name="terminationDate"
              :label="$t('employee.terminate.lastDayLabel')"
              rules="required"
              :disabled="saving"
              :validate-immediate="false"
            />
            <page-sub-footer>
              <b-button data-testid="terminate-button" type="submit" variant="danger" :disabled="v.invalid || saving">
                <b-spinner v-if="saving" class="mr-2" small />
                {{ saving ? $t('employee.terminate.submittingButton') : $t('employee.terminate.submitButton') }}
              </b-button>
            </page-sub-footer>
          </b-form>
        </ValidationObserver>
      </template>
    </template>
    <sme-alert v-if="saved" level="good">
      <p>
        <strong>{{ $t('employee.terminate.terminatedTitle', { name: employee.full_name }) }}</strong>
      </p>
      <!-- Both outcomes are a success; only the banking sentence differs. -->
      <p v-if="unenrolmentOutcome === PORTAL_UNENROLMENT_OUTCOME.INITIATED">
        {{ $t('employee.terminate.removedWithBankingBody') }}
      </p>
      <p v-else>{{ $t('employee.terminate.removedBody') }}</p>
      <p>
        {{ $t('employee.terminate.reverseHintBody') }}
      </p>
    </sme-alert>
    <!-- Only offered when banking requires manually unenrolling -->
    <div v-if="showCompletionStep" class="mt-3">
      <template v-if="readyToCompleteUnenrolment">
        <sme-alert level="info" class="mb-3">
          {{ $t('employee.terminate.finishReturningAlert') }}
        </sme-alert>
        <div class="d-flex justify-content-center">
          <router-link
            tag="button"
            class="btn btn-primary"
            :to="{ name: 'employee-complete-unenrolment', params: { employeeId: employee.employee_id } }"
            >{{ $t('employee.actions.completeTerminationButton') }}</router-link
          >
        </div>
      </template>
      <sme-alert v-else level="info">
        {{ $t('employee.terminate.pendingBankingAlert') }}
      </sme-alert>
    </div>
  </app-page>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import State from '@/state/State';
import { canCompleteBankingReturn, getTerminateUnavailableReason, PORTAL_UNENROLMENT_OUTCOME } from '@/utils/Employee';
import { DEFAULT_PARTNERSHIP_NAME, getPartnershipData } from '@/utils/partnership';
import { TODO_ACTIONS } from '@/utils/Todo';

const { name: partnershipName } = getPartnershipData();

export default {
  name: 'EmployeeTerminate',
  components: { ValidationObserver, AppInput, AppLoading, AppPage, PageSubFooter, SmeAlert },
  data() {
    return {
      PORTAL_UNENROLMENT_OUTCOME,
      productName: partnershipName || DEFAULT_PARTNERSHIP_NAME,
      loading: true,
      saving: false,
      saved: false,
      error: undefined,
      employee: undefined,
      terminationDate: undefined,
      unenrolmentOutcome: undefined,
    };
  },
  computed: {
    unavailableReason() {
      return getTerminateUnavailableReason(this.employee);
    },
    showCompletionStep() {
      // NOT_REQUIRED means there was no beneficiary banking to push back, so there is nothing to
      // complete
      return (
        this.saved && this.unenrolmentOutcome === PORTAL_UNENROLMENT_OUTCOME.INITIATED && canCompleteBankingReturn()
      );
    },
    readyToCompleteUnenrolment() {
      // The backend only raises todo when employee has no outstanding transfers, so its
      // absence is what distinguishes "not yet" from "ready".
      return State.state.todos.some(
        todo => todo.action === TODO_ACTIONS.DISABLE_EMPLOYEE && todo.fk === this.employee?.employee_id,
      );
    },
  },
  methods: {
    async onSubmit() {
      this.saving = true;

      try {
        const result = await ApiClient.employeeUnenrol(this.employee.employee_id, this.terminationDate);
        this.unenrolmentOutcome = result?.unenrolment;

        // The unenrolment raises the banking-return todo, which the completion step reads from
        // state, so it has to be refreshed for the step to appear without a page load. Swallowed
        // because the termination itself has already succeeded: the worst case is the holding
        // message instead of the button, and the employee page offers the same action.
        try {
          await ApiClient.getUser();
        } catch {
          // Intentionally ignored.
        }

        this.saved = true;
      } catch (error) {
        this.error = error.message;
      }

      this.saving = false;
    },
    loadData() {
      this.loading = true;
      ApiClient.getEmployee(this.$route.params.employeeId).then(
        employee => {
          this.employee = employee;
          this.loading = false;
        },
        error => {
          this.error = error.message;
          this.loading = false;
        },
      );
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>
